import { useState } from 'react';
import { ChevronDown, ChevronRight, Check } from 'lucide-react';

const Dot = ({ done }) => <span className={`w-4 h-4 rounded-full inline-flex items-center justify-center shrink-0 border ${done ? 'bg-emerald-500 border-emerald-500 text-white' : 'border-[var(--c-border-strong)] bg-[var(--c-surface-0)]'}`}>{done && <Check className="w-2.5 h-2.5" strokeWidth={3} />}</span>;

/**
 * StepRail — rail vertical d'étapes partagé (éditeur de lot, tracé des bâtiments).
 * steps: [{ id, n, label, icon, done, subs: [{ id, label, icon, done, meta }], extra }]
 * active / activeSub : identifiants courants · onSelect(step, sub|null) · navigation libre.
 */
export default function StepRail({ title = 'Étapes', steps, active, activeSub, onSelect, width = 232, testId = 'step-rail' }) {
  const [open, setOpen] = useState({});
  const isOpen = (id) => open[id] ?? id === active;
  return (
    <nav className="shrink-0 min-h-0 overflow-y-auto rounded-lg border border-[var(--c-border)] bg-[var(--c-surface-0)] py-1" style={{ width }} data-testid={testId} aria-label={title}>
      <div className="px-3 h-8 flex items-center text-[10px] uppercase tracking-[0.18em] text-[var(--c-text-faint)]">{title}</div>
      {steps.map((st) => {
        const done = typeof st.done === 'boolean' ? st.done : st.subs.length > 0 && st.subs.every((s) => s.done);
        const cur = st.id === active;
        const hasSubs = st.subs.length > 0 || !!st.extra;
        return (
          <div key={st.id} className="mb-0.5">
            <div className={`flex items-center gap-2 h-9 pl-2 pr-1.5 mx-1 rounded-md cursor-pointer transition-colors ${cur ? 'bg-[var(--c-gold-soft)]' : 'hover:bg-[var(--c-surface-2)]'}`} onClick={() => onSelect(st, null)} data-testid={`rail-step-${st.id}`} aria-current={cur ? 'step' : undefined}>
              <Dot done={done} />
              <span className={`text-[12px] flex-1 truncate ${cur ? 'font-semibold text-[var(--c-text)]' : 'text-[var(--c-text-2)]'}`}>{st.n} · {st.label}</span>
              {hasSubs && <button type="button" onClick={(e) => { e.stopPropagation(); setOpen((o) => ({ ...o, [st.id]: !isOpen(st.id) })); }} className="w-5 h-5 inline-flex items-center justify-center text-[var(--c-text-faint)] hover:text-[var(--c-text)]" data-testid={`rail-toggle-${st.id}`}>{isOpen(st.id) ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}</button>}
            </div>
            {hasSubs && isOpen(st.id) && (
              <div className="pb-1">
                {st.subs.map((s) => {
                  const selected = cur && (activeSub === s.id || (!activeSub && st.subs[0].id === s.id));
                  const Icon = s.icon;
                  return (
                    <button key={s.id} type="button" onClick={() => onSelect(st, s)} className={`w-full flex items-center gap-2 h-7 pl-9 pr-2 text-left text-[11px] transition-colors ${selected ? 'text-[var(--c-gold)] font-medium' : 'text-[var(--c-text-muted)] hover:text-[var(--c-text)]'}`} data-testid={`rail-sub-${s.id}`}>
                      <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${s.done ? 'bg-emerald-500' : 'bg-[var(--c-border-strong)]'}`} />{Icon && <Icon className="w-3 h-3 opacity-70" />}<span className="truncate flex-1">{s.label}</span>{s.meta && <span className="text-[9.5px] hud-tabular text-[var(--c-text-faint)]">{s.meta}</span>}
                    </button>
                  );
                })}
                {st.extra}
              </div>
            )}
          </div>
        );
      })}
    </nav>
  );
}
